"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { Bug, CheckCircle2, Lightbulb, MessageSquare, type LucideIcon } from "lucide-react";
import { submitFeedback } from "@/lib/analytics";

/**
 * Floating global feedback button + popover card.
 *
 * Mounted once in app/layout.tsx. Auto-hides on exam-taking pages
 * ( /{exam}/exam/[id] ) to avoid distracting users during a timed exam.
 *
 * UX rules:
 * - Category chips are OPTIONAL (user can leave category null)
 * - Message textarea is OPTIONAL
 * - Submit enabled as long as EITHER category or message is filled
 * - Auto-attaches: current URL
 */

const CATEGORIES: { value: string; label: string; icon: LucideIcon }[] = [
  { value: "bug",     label: "Bug",   icon: Bug },
  { value: "idea",    label: "Idea",  icon: Lightbulb },
  { value: "other",   label: "Other", icon: MessageSquare },
];

function shouldHideOnPath(path: string): boolean {
  // Hide while a user is actively taking an exam:  /{exam}/exam/[id]
  // Pattern: /anything/exam/anything
  return /^\/[^/]+\/exam\/[^/]+/.test(path);
}

export function FeedbackButton() {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);

  if (pathname && shouldHideOnPath(pathname)) return null;

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Send feedback"
          className="fixed bottom-4 right-4 z-40 flex items-center gap-2 bg-gray-900 text-white text-sm font-medium px-4 py-2.5 rounded-full shadow-lg hover:bg-gray-800 transition-colors group"
        >
          <MessageSquare className="w-4 h-4" strokeWidth={1.75} aria-hidden="true" />
          <span className="hidden group-hover:inline-block">Feedback</span>
        </button>
      )}

      {/* Popover card */}
      {open && <FeedbackCard onClose={() => setOpen(false)} />}
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  The card itself                                                    */
/* ------------------------------------------------------------------ */

function FeedbackCard({ onClose }: { onClose: () => void }) {
  const [category, setCategory] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Close on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Auto-close after successful submit
  useEffect(() => {
    if (!sent) return;
    const t = setTimeout(onClose, 1500);
    return () => clearTimeout(t);
  }, [sent, onClose]);

  const canSubmit =
    !sending && (category !== null || message.trim().length > 0);

  async function handleSubmit() {
    if (!canSubmit) return;
    setSending(true);
    setError(null);
    const res = await submitFeedback({
      kind: "global",
      category: category ?? undefined,
      message: message.trim() || undefined,
      context: {
        url: typeof window !== "undefined" ? window.location.pathname : null,
      },
    });
    setSending(false);
    if (res.ok) {
      setSent(true);
    } else {
      setError("Couldn't send — try again?");
    }
  }

  return (
    <div
      className="fixed bottom-4 right-4 z-50 w-80 bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-800 overflow-hidden"
      role="dialog"
      aria-label="Send feedback"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800">
        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Send feedback
        </div>
        <button
          onClick={onClose}
          aria-label="Close"
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-lg leading-none"
        >
          ×
        </button>
      </div>

      {sent ? (
        /* Success state */
        <div className="px-4 py-8 text-center">
          <div className="mb-2 flex justify-center">
            <CheckCircle2 className="w-6 h-6 text-green-600" strokeWidth={1.75} aria-hidden="true" />
          </div>
          <div className="text-sm text-gray-700 dark:text-gray-300">
            Thanks — we&apos;ll take a look!
          </div>
        </div>
      ) : (
        /* Input state */
        <div className="px-4 py-3">
          {/* Category chips — optional */}
          <div className="flex gap-2 mb-3">
            {CATEGORIES.map((c) => {
              const selected = category === c.value;
              return (
                <button
                  key={c.value}
                  onClick={() => setCategory(selected ? null : c.value)}
                  className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    selected
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-gray-300"
                  }`}
                >
                  <c.icon className="w-4 h-4" strokeWidth={1.75} aria-hidden="true" />
                  {c.label}
                </button>
              );
            })}
          </div>

          {/* Message textarea — optional */}
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="What's on your mind? (optional)"
            rows={4}
            className="w-full text-sm border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          {error && (
            <div className="text-xs text-red-600 mt-2">{error}</div>
          )}

          <div className="flex items-center justify-between mt-3">
            <div className="text-[10px] text-gray-400">
              We&apos;ll include the current page URL.
            </div>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 dark:disabled:bg-gray-800 dark:disabled:text-gray-600 transition-colors"
            >
              {sending ? "Sending…" : "Send"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
